import React, { useState, useEffect } from 'react';
import { TextField, MenuItem, CircularProgress, InputAdornment, ListSubheader, Chip, Box } from '@mui/material';
import { internalChannelsApi } from '../services/api';
import { authService } from '../services/auth';
import { callerIdService } from '../services/callerIdService';
import { CallerIdVerification } from '../types';

export interface PhoneChannel {
  channel_id: string;
  channel_value: string;
  channel_name: string;
  channel_type: string;
  voice_enabled: boolean;
}

// Global cache for phone channels
let channelsCache: PhoneChannel[] | null = null;
let fetchingPromise: Promise<PhoneChannel[]> | null = null;

export const fetchPhoneChannels = async (): Promise<PhoneChannel[]> => {
  // Return empty array if not authenticated
  if (!authService.isAuthenticated()) {
    return [];
  }

  if (channelsCache) return channelsCache;
  if (fetchingPromise) return fetchingPromise;

  fetchingPromise = internalChannelsApi.getInternalChannels().then((results: any) => {
    // Filter only phone channels that have voice enabled
    const phoneChannels = results.filter((channel: PhoneChannel) =>
      channel.channel_type === 'phone' && channel.voice_enabled
    );
    channelsCache = phoneChannels;
    fetchingPromise = null;
    return phoneChannels;
  }).catch((err) => {
    console.error('Error fetching phone channels:', err);
    fetchingPromise = null;
    throw err;
  });

  return fetchingPromise;
};

// Only prefetch if authenticated
if (authService.isAuthenticated()) {
  fetchPhoneChannels().catch(console.error);
}

interface PhoneNumberSelectProps {
  value: string;
  onChange: (value: string, channelId: string) => void;
  label?: string;
  fullWidth?: boolean;
  channels?: PhoneChannel[];
  showAllOption?: boolean;
  sx?: any;
  disabled?: boolean;
  includeVerifiedCallerIds?: boolean;
}

const PhoneNumberSelect: React.FC<PhoneNumberSelectProps> = ({
  value,
  onChange,
  label = 'From Number',
  fullWidth = true,
  channels = channelsCache || [],
  showAllOption = false,
  sx,
  disabled,
  includeVerifiedCallerIds = false,
}) => {
  const [loading, setLoading] = useState<boolean>(!channelsCache && authService.isAuthenticated());
  const [verifiedNumbers, setVerifiedNumbers] = useState<CallerIdVerification[]>([]);

  useEffect(() => {
    if (channelsCache) {
      setLoading(false);
      return;
    }
    if (fetchingPromise) {
      fetchingPromise.finally(() => setLoading(false));
    } else if (!channelsCache && authService.isAuthenticated()) {
      setLoading(true);
      fetchPhoneChannels().finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => {
    if (includeVerifiedCallerIds && authService.isAuthenticated()) {
      callerIdService.listVerified()
        .then((resp) => {
          setVerifiedNumbers(resp.data.results?.verified_numbers || []);
        })
        .catch((err) => {
          console.error('Error fetching verified caller IDs:', err);
        });
    }
  }, [includeVerifiedCallerIds]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedChannel = channels.find(channel => channel.channel_value === e.target.value);
    onChange(e.target.value, selectedChannel?.channel_id || '');
  };

  const hasVerifiedNumbers = includeVerifiedCallerIds && verifiedNumbers.length > 0;

  return (
    <TextField
      select
      fullWidth={fullWidth}
      label={label}
      value={value}
      onChange={handleChange}
      sx={sx}
      disabled={disabled || loading}
      InputProps={{
        startAdornment: loading ? (
          <InputAdornment position="start">
            <CircularProgress size={16} />
          </InputAdornment>
        ) : undefined,
      }}
      SelectProps={{
        renderValue: (selected) => selected as string
      }}
    >
      {showAllOption && <MenuItem value="">All</MenuItem>}
      {hasVerifiedNumbers && (
        <ListSubheader sx={{ fontWeight: 600, color: 'text.primary' }}>
          Your Business Numbers (Verified Caller ID)
        </ListSubheader>
      )}
      {hasVerifiedNumbers && verifiedNumbers.map((num) => (
        <MenuItem key={`verified-${num.phone_number}`} value={num.phone_number}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <span>{num.friendly_name || num.phone_number} ({num.phone_number})</span>
            <Chip label="Calls only" size="small" variant="outlined" sx={{ ml: 'auto', fontSize: '0.7rem', height: 20 }} />
          </Box>
        </MenuItem>
      ))}
      {hasVerifiedNumbers && (
        <ListSubheader sx={{ fontWeight: 600, color: 'text.primary' }}>
          Yobi Numbers
        </ListSubheader>
      )}
      {channels.map((channel) => (
        <MenuItem key={channel.channel_id} value={channel.channel_value}>
          {channel.channel_name} ({channel.channel_value})
        </MenuItem>
      ))}
    </TextField>
  );
};

export default PhoneNumberSelect;
